import { useState, useEffect } from 'react';
import CategoriesTab from '../Role-based/CategoriesTab';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify'; // Import toastify components
import 'react-toastify/dist/ReactToastify.css'; // Import the necessary CSS for toastify
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const CategoriesTable = () => {
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newSubcategory, setNewSubcategory] = useState({
    categoryId: '',
    name: '',
  });
  const [editSubcategory, setEditSubcategory] = useState({
    id: '',
    categoryId: '',
    name: '',
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/subcategories/`);
        if (!response.ok) {
          throw new Error('Failed to fetch categories');
        }
        const data = await response.json();

        const groupedCategories = data.reduce((acc, item) => {
          if (!acc[item.category_name]) {
            acc[item.category_name] = [];
          }
          acc[item.category_name].push({
            id: item.id,
            name: item.name,
            categoryId: item.categoryId,
          });
          return acc;
        }, {});

        const categoriesArray = Object.keys(groupedCategories).map((categoryName) => ({
          category: categoryName,
          subcategories: groupedCategories[categoryName],
        }));

        setCategories(categoriesArray);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);

  const fetchCategoriesForDropdown = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/categories/`);
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();
      setSubcategories(data);
    } catch (error) {
      console.error('Error fetching categories for dropdown:', error);
    }
  };

  const handleModalOpen = () => {
    fetchCategoriesForDropdown();
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setNewSubcategory({
      categoryId: '',
      name: '',
    });
  };

  const handleEditModalOpen = (subcategory) => {
    setEditSubcategory({
      id: subcategory.id,
      categoryId: subcategory.categoryId,
      name: subcategory.name,
    });
    fetchCategoriesForDropdown();
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditSubcategory({
      id: '',
      categoryId: '',
      name: '',
    });
  };

  const handleDeleteModalOpen = (subcategory) => {
    setSubcategoryToDelete(subcategory);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setSubcategoryToDelete(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSubcategory((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditSubcategory((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const fetchCategoriesData = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/subcategories/`);
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();

      const groupedCategories = data.reduce((acc, item) => {
        if (!acc[item.category_name]) {
          acc[item.category_name] = [];
        }
        acc[item.category_name].push({
          id: item.id,
          name: item.name,
          categoryId: item.categoryId,
        });
        return acc;
      }, {});

      const categoriesArray = Object.keys(groupedCategories).map((categoryName) => ({
        category: categoryName,
        subcategories: groupedCategories[categoryName],
      }));

      setCategories(categoriesArray);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Use it in useEffect
  useEffect(() => {
    fetchCategoriesData();
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const { categoryId, name } = newSubcategory;

    if (!categoryId || !name) {
      toast.error('Please select a category and enter a subcategory name.');
      return;
    }

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/subcategories/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          name: name,
          categoryId: categoryId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create subcategory');
      }

      const result = await response.json();
      toast.success(`Subcategory '${result.name}' created successfully!`);

      // Refresh categories data
      await fetchCategoriesData();

      handleModalClose();
    } catch (error) {
      console.error('Error creating subcategory:', error);
      toast.error('Error creating subcategory.');
    }
  };

  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    const { id, categoryId, name } = editSubcategory;

    if (!id || !categoryId || !name) {
      toast.error('Please fill all fields.');
      return;
    }

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/subcategories/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          name: name,
          categoryId: categoryId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update subcategory');
      }

      const result = await response.json();
      toast.success(`Subcategory '${result.name}' updated successfully!`);

      // Refresh categories data
      await fetchCategoriesData();

      handleEditModalClose();
    } catch (error) {
      console.error('Error updating subcategory:', error);
      toast.error('Error updating subcategory.');
    }
  };

  const handleDelete = async () => {
    if (!subcategoryToDelete) return;

    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/subcategories/${subcategoryToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete subcategory');
      }

      toast.success(`Subcategory '${subcategoryToDelete.name}' deleted successfully!`);

      handleDeleteModalClose();
      setCategories((prevCategories) =>
        prevCategories.map((category) => ({
          ...category,
          subcategories: category.subcategories.filter(
            (subcategory) => subcategory.id !== subcategoryToDelete.id
          ),
        }))
      );
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      toast.error('Error deleting subcategory.');
    }
  };

  const filteredCategories = categories.map((category) => ({
    ...category,
    subcategories: category.subcategories.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase())
    ),
  }));

  return (
    <div className="max-w-8xl mx-auto p-4">
      <div className="flex justify-between items-center mt-2 mb-4">
        <h1 className="text-lg text-black font-bold">Create or Edit Sub-categories</h1>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <input
          type="text"
          placeholder="Search categories..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border p-2 w-1/3 rounded-md"
        />
        <button
          className="bg-black text-white px-4 py-2 rounded-md shadow hover:text-white"
          onClick={handleModalOpen}
        >
          + Create New Subcategory
        </button>
      </div>

      {filteredCategories.map((category, index) => (
        <div key={index} className="bg-white shadow-md rounded-md p- mb-6">
          <h2 className="text-lg font-bold border-b pb-1 p-2 mb-">{category.category}</h2>
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left text-sm font-semibold text-gray-600 border-b">
                <th className="py-2 px-4 w-1/6">Sr. No.</th>
                <th className="py-2 px-4 w-3/6">Subcategory</th>
                <th className="py-2 px-4 w-2/6 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {category.subcategories.length > 0 ? (
                category.subcategories.map((item, idx) => (
                  <tr key={idx} className="border-b text-gray-700">
                    <td className="py-2 px-4">{idx + 1}</td>
                    <td className="py-2 text-md font-semibold px-4">{item.name}</td>
                    <td className="py-2 px-4 text-center flex gap-6 justify-center">
                      <FaEdit
                        size={20}
                        className="text-blue-500 cursor-pointer"
                        onClick={() => handleEditModalOpen(item)}
                      />
                      <FaTrash
                        size={20}
                        className="text-red-500 cursor-pointer"
                        onClick={() => handleDeleteModalOpen(item)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="py-2 px-4 text-gray-500" colSpan="3">
                    No subcategories found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      {/* Modal for delete confirmation */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-md shadow-lg w-[400px]  max-w-screen">
            <h2 className="text-xl font-semibold text-center mb-6 text-gray-800">
              Are you sure you want to delete the subcategory:{' '}
              <span className="font-bold">{subcategoryToDelete.name}</span>?
            </h2>
            {/* <p className="text-center text-gray-600 mb-6">
        This action is irreversible. Please confirm if you want to proceed.
      </p> */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={handleDeleteModalClose}
                className="bg-black text-white hover:text-white px-6 py-2 rounded-md transition duration-200 ease-in-out"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="bg-red-500 text-white hover:text-black px-6 py-2 rounded-md transition duration-200 ease-in-out"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for creating new subcategory */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-md shadow-md w-1/3">
            <h2 className="text-xl font-semibold mb-4">Create New Subcategory</h2>
            <form onSubmit={handleFormSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Select Category</label>
                <select
                  name="categoryId"
                  value={newSubcategory.categoryId}
                  onChange={handleInputChange}
                  className="border p-2 w-full rounded-md"
                >
                  <option value="">Select a Category</option>
                  {subcategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Subcategory Name</label>
                <input
                  type="text"
                  name="name"
                  value={newSubcategory.name}
                  onChange={handleInputChange}
                  placeholder="Enter subcategory name"
                  className="border p-2 w-full rounded-md"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="bg-red-500 text-white hover:text-black px-6 py-2 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-black hover:text-white text-white px-6 py-2 rounded-md"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for editing subcategory */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-md shadow-md w-1/3">
            <h2 className="text-xl font-semibold mb-4">Edit Subcategory</h2>
            <form onSubmit={handleEditFormSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Select Category</label>
                <select
                  name="categoryId"
                  value={editSubcategory.categoryId}
                  onChange={handleEditInputChange}
                  className="border p-2 w-full rounded-md"
                >
                  <option value="">Select a Category</option>
                  {subcategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Subcategory Name</label>
                <input
                  type="text"
                  name="name"
                  value={editSubcategory.name}
                  onChange={handleEditInputChange}
                  placeholder="Enter subcategory name"
                  className="border p-2 w-full rounded-md"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleEditModalClose}
                  className="bg-red-500 text-white hover:text-black px-6 py-2 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-black hover:text-white text-white px-6 py-2 rounded-md"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast container */}
      <ToastContainer />
    </div>
  );
};

export default CategoriesTable;
